import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { CreditCard, CheckCircle2, AlertTriangle, Trash2 } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { Field } from "./Common";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

const Box = ({ title, testId, children }) => (
  <div className="rounded-[var(--radius)] border bg-card p-5 space-y-3" data-testid={testId}>
    <div className="flex items-center gap-2 font-semibold"><CreditCard className="h-5 w-5 text-primary" />{title}</div>{children}
  </div>
);
const Ok = ({ children, testId }) => <p className="flex items-center gap-2 text-sm text-emerald-700" data-testid={testId}><CheckCircle2 className="h-4 w-4" />{children}</p>;
const Warn = ({ children, testId }) => <p className="flex items-start gap-2 text-sm text-amber-800" data-testid={testId}><AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />{children}</p>;

function StripeKeyForm({ own, reload }) {
  const [key, setKey] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await api.put("/payment-accounts/stripe-key", { secret_key: key }); toast.success("Stripe key saved"); setKey(""); reload(); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const remove = async () => {
    if (!window.confirm("Remove your Stripe key from Bloomwell?")) return;
    await api.delete("/payment-accounts/stripe-key").then(reload).catch((e) => toast.error(errMsg(e)));
  };
  if (own.connected) return (
    <div className="space-y-2 border-t pt-3" data-testid="stripe-key-connected">
      <Ok testId="stripe-key-ok">Using your own Stripe key{own.name ? `: ${own.name}` : ""} ({own.key_hint}, {own.mode === "live" ? "live" : "test mode"}). Online payments go straight into this account.</Ok>
      {!own.webhook && <p className="text-xs text-muted-foreground">Bloomwell couldn't add its payment notification in your Stripe account, so it checks payments when patients come back from Stripe instead.</p>}
      <Button variant="ghost" className="rounded-full" data-testid="stripe-key-remove-btn" onClick={remove}>Remove key</Button>
    </div>
  );
  return (
    <div className="space-y-2 border-t pt-3" data-testid="stripe-key-form">
      <div className="font-medium text-sm">Or paste your own Stripe key</div>
      <p className="text-xs text-muted-foreground">In Stripe: Developers → API keys → Secret key → Reveal, then copy it (starts with sk_live_). It's stored encrypted and never shown again.</p>
      <div className="flex flex-wrap gap-2">
        <Input type="password" autoComplete="off" className="max-w-sm" placeholder="sk_live_…" data-testid="stripe-key-input" value={key} onChange={(e) => setKey(e.target.value)} />
        <Button className="rounded-full" disabled={busy || !key} data-testid="stripe-key-save-btn" onClick={save}>Save key</Button>
      </div>
    </div>
  );
}

function StripeBox({ s, own, reload }) {
  const [busy, setBusy] = useState(false);
  const connect = async () => {
    setBusy(true);
    try { const r = await api.post("/payment-accounts/stripe/connect", { origin_url: window.location.origin }); window.location.assign(r.data.url); }
    catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };
  const disconnect = async () => {
    if (!window.confirm("Disconnect this Stripe account? Online payments will go to the Bloomwell account again.")) return;
    await api.delete("/payment-accounts/stripe").then(reload).catch((e) => toast.error(errMsg(e)));
  };
  return (
    <Box title="Stripe: online card payments" testId="payments-stripe-box">
      <p className="text-sm text-muted-foreground">Used for online booking deposits, home visits, packages, gift cards and the shop.</p>
      {own.connected ? null : s.charges_enabled ? <Ok testId="stripe-connected">Connected{s.name ? `: ${s.name}` : ""}. Online payments go straight into your Stripe account.</Ok>
        : s.connected ? <Warn testId="stripe-incomplete">Your Stripe setup isn't finished yet. Until it is, online payments go to the Bloomwell account.</Warn>
          : <Warn testId="stripe-not-connected">Not connected. Online payments currently go to the Bloomwell account.</Warn>}
      {s.platform_mode === "test" && !own.connected && <p className="text-xs text-muted-foreground" data-testid="stripe-test-mode">Bloomwell is in Stripe test mode, so no real money moves yet.</p>}
      {!own.connected && <><div className="flex flex-wrap gap-2">
        {!s.charges_enabled && <Button className="rounded-full" disabled={busy} data-testid="stripe-connect-btn" onClick={connect}>{s.connected ? "Finish Stripe setup" : "Connect with Stripe"}</Button>}
        {s.connected && <Button variant="ghost" className="rounded-full" data-testid="stripe-disconnect-btn" onClick={disconnect}>Disconnect</Button>}
      </div>
      <p className="text-xs text-muted-foreground">You'll be taken to Stripe. Sign in with your existing Stripe login (or create one) and add your bank details. With this button you don't need to copy any Stripe keys.</p></>}
      <StripeKeyForm own={own} reload={reload} />
    </Box>
  );
}

function SumUpConnect({ reload }) {
  const [f, setF] = useState({ merchant_code: "", api_key: "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await api.put("/payment-accounts/sumup", f); toast.success("SumUp connected"); reload(); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <div className="space-y-3">
      <p className="text-xs text-muted-foreground">In SumUp: me.sumup.com → Settings → For Developers → Toolkit → API Keys → Create. Copy the key straight away, because SumUp only shows it once. Your merchant code starts with "M" and is shown in your SumUp profile.</p>
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Merchant code"><Input data-testid="sumup-merchant-code-input" value={f.merchant_code} onChange={(e) => setF({ ...f, merchant_code: e.target.value })} /></Field>
        <Field label="API key"><Input type="password" autoComplete="off" data-testid="sumup-api-key-input" value={f.api_key} onChange={(e) => setF({ ...f, api_key: e.target.value })} /></Field>
      </div>
      <Button className="rounded-full" disabled={busy || !f.merchant_code || !f.api_key} data-testid="sumup-connect-btn" onClick={save}>Connect SumUp</Button>
    </div>
  );
}

function ReaderSetup({ su, reload }) {
  const [k, setK] = useState({ affiliate_key: "", affiliate_app_id: su.affiliate_app_id || "" });
  const [pair, setPair] = useState({ pairing_code: "", name: "Front desk" });
  const [readers, setReaders] = useState([]);
  const loadReaders = useCallback(() => api.get("/sumup/readers").then((r) => setReaders(r.data)).catch(() => setReaders([])), []);
  useEffect(() => { loadReaders(); }, [loadReaders]);
  const saveKey = () => api.put("/payment-accounts/sumup/reader", k).then(() => { toast.success("Card reader key saved"); reload(); }).catch((e) => toast.error(errMsg(e)));
  const doPair = () => api.post("/sumup/readers", pair).then(() => { toast.success("Reader paired"); setPair({ ...pair, pairing_code: "" }); loadReaders(); }).catch((e) => toast.error(errMsg(e)));
  const remove = (id) => api.delete(`/sumup/readers/${id}`).then(loadReaders).catch((e) => toast.error(errMsg(e)));
  return (
    <div className="space-y-3 border-t pt-3" data-testid="sumup-reader-setup">
      <div className="font-medium text-sm">Card reader (SumUp Solo)</div>
      <p className="text-xs text-muted-foreground">1. In SumUp: For Developers → Toolkit → Affiliate Keys → create a key and note the app identifier you entered. 2. On the Solo: Connections → API → Connect, then type the code it shows below (valid for 5 minutes).</p>
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Affiliate key"><Input type="password" autoComplete="off" data-testid="sumup-affiliate-key-input" placeholder={su.reader_ready ? "Saved (enter to replace)" : ""} value={k.affiliate_key} onChange={(e) => setK({ ...k, affiliate_key: e.target.value })} /></Field>
        <Field label="App identifier"><Input data-testid="sumup-affiliate-app-input" value={k.affiliate_app_id} onChange={(e) => setK({ ...k, affiliate_app_id: e.target.value })} /></Field>
      </div>
      <Button size="sm" variant="outline" className="rounded-full" disabled={!k.affiliate_key || !k.affiliate_app_id} data-testid="sumup-affiliate-save" onClick={saveKey}>Save card reader key</Button>
      <div className="flex flex-wrap items-end gap-2">
        <Field label="Pairing code"><Input className="w-36" data-testid="sumup-pairing-code-input" value={pair.pairing_code} onChange={(e) => setPair({ ...pair, pairing_code: e.target.value })} /></Field>
        <Field label="Reader name"><Input className="w-40" data-testid="sumup-reader-name-input" value={pair.name} onChange={(e) => setPair({ ...pair, name: e.target.value })} /></Field>
        <Button size="sm" className="rounded-full" disabled={!pair.pairing_code} data-testid="sumup-pair-btn" onClick={doPair}>Pair reader</Button>
      </div>
      {readers.map((r) => (
        <div key={r.id} className="flex items-center justify-between text-sm border-t pt-2" data-testid={`sumup-reader-${r.id}`}>
          <span>{r.name || r.id} <span className="text-xs text-muted-foreground">{r.model} {r.status}</span></span>
          <Button size="icon" variant="ghost" data-testid={`sumup-reader-remove-${r.id}`} onClick={() => remove(r.id)}><Trash2 className="h-4 w-4" /></Button>
        </div>
      ))}
    </div>
  );
}

function SumUpBox({ su, reload }) {
  const disconnect = async () => {
    if (!window.confirm("Disconnect SumUp from this clinic?")) return;
    await api.delete("/payment-accounts/sumup").then(reload).catch((e) => toast.error(errMsg(e)));
  };
  return (
    <Box title="SumUp: payment links, QR and card reader" testId="payments-sumup-box">
      <p className="text-sm text-muted-foreground">From any appointment, staff can send a SumUp payment link, show a QR code for the patient to scan, or send the amount to your SumUp card reader. Payments go straight into your SumUp account.</p>
      {su.connected ? <>
        <Ok testId="sumup-connected">Connected: {su.name || su.merchant_code} ({su.merchant_code})</Ok>
        <ReaderSetup su={su} reload={reload} />
        <Button variant="ghost" className="rounded-full" data-testid="sumup-disconnect-btn" onClick={disconnect}>Disconnect SumUp</Button>
      </> : <SumUpConnect reload={reload} />}
      <p className="text-xs text-muted-foreground">Tap to Pay on your phone works in the SumUp app itself. Bloomwell runs in the browser, so it can't open Tap to Pay directly. Record those payments with "Take payment" → SumUp.</p>
    </Box>
  );
}

export function PaymentSettings() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const load = useCallback(() => api.get("/payment-accounts").then((r) => setD(r.data)).catch((e) => setErr(errMsg(e))), []);
  useEffect(() => { load(); }, [load]);
  if (err) return <p className="text-sm text-muted-foreground" data-testid="payment-settings-error">{err}</p>;
  if (!d) return <p className="text-sm text-muted-foreground">Loading…</p>;
  return (
    <div className="grid lg:grid-cols-2 gap-4" data-testid="payment-settings">
      <StripeBox s={d.stripe} own={d.stripe_key} reload={load} />
      <SumUpBox su={d.sumup} reload={load} />
    </div>
  );
}
